import React, {useEffect, useState, useTransition} from 'react';
import {useSelector} from "react-redux";

import {useAppKitProvider} from "@reown/appkit/react";
import {useTranslation} from "react-i18next";
import {format} from "date-fns";
import {ethers} from "ethers";

import {
    INFO,
    SIZE,
    ERROR,
    SUCCESS,
    INFORMATION,
    INTERACTION,
    STEPS_STATUS,
    INPUT_VARIANT,
    BUTTON_VARIANT,
    MAKE_OFFER_TYPE,
    WALLET_ERROR_CODE,
    LISTING_DAY_OPTIONS
} from "utils";
import {useCreateOfferMutation, useUpdateOfferMutation} from "store/Bazaar/bazaar.api";
import {ERC20, KOMPETEMarketplaceV2ABI} from "web3/ABI";
import {useActionContext} from "../useActionContext";
import {CHAIN_TO_PARAMS_MAP} from "web3/chains";
import {useConnectedWallet} from "hooks";
import {useModalsContext} from "layouts";

import {ButtonV2, Information, InputV2, Modal, SelectDropDownDisplay} from "components/atoms";
import {Counter, Process, InfoItem} from "./components";

import "../../style.scss"

const BazaarCurrentItemOfferModal = ({modal}) => {
    const {isConnected, isConnectedAddressCorrect,connectedChainId} = useConnectedWallet()
    const [isPending, startTransition] = useTransition()
    const {setCurrentModal, currentModal} = useModalsContext()
    const {walletProvider} = useAppKitProvider("eip155");
    const {setActionDetails} = useActionContext()
    const {t} = useTranslation()

    const {userData} = useSelector(state => state.authV2.signIn)
    const {name, tokenId, chain} = currentModal.data

    const [informationStatus, setInformationStatus] = useState({
        type: INFORMATION.INFO,
        text: INFO.OFFER_CREATE
    })

    const [offerPrice, setOfferPrice] = useState("")
    const [expiration, setExpiration] = useState(null)
    const [count, setCount] = useState(1)

    const [makeOffer] = useCreateOfferMutation()
    const [updateOffer] = useUpdateOfferMutation()

    const isMakeOfferModal = modal === STEPS_STATUS.MAKE_OFFER
    const isConnectedChainCorrect = isConnected && (connectedChainId === Number(chain))

    useEffect(() => {
        if (!isConnectedChainCorrect) {
            setInformationStatus({
                    type: INFORMATION.ERROR,
                    text: ERROR.INVALID_CHAIN
                }
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const amountHandler = (event) => {
        const {value} = event.target
        setOfferPrice(value)

        if (value > userData.kompeteBalance) {
            setInformationStatus({
                type: INFORMATION.ERROR,
                text: ERROR.INSUFFICIENT_KOMPETE_BALANCE
            })
        } else {
            setInformationStatus({
                type: INFORMATION.INFO,
                text: INFO.OFFER_CREATE
            })
        }
    }

    const isMatchableWallet = (connectedWallet) => {
        const registeredWallet = userData.wallet.toLowerCase()
        return connectedWallet.toLowerCase() === registeredWallet;
    }

    const handleOffer = () => {
        startTransition(async () => {
            try {
                const currentChainId = isMakeOfferModal ? chain : currentModal.data.chain
                const currentTokenId = isMakeOfferModal ? tokenId : currentModal.data.inventory.token_id
                const {rpcURL, addresses} = CHAIN_TO_PARAMS_MAP[currentChainId]

                const {MARKETPLACE_ADDRESS_V2, COLLECTION_ADDRESS, PAYMENT_ADDRESS} = addresses
                const provider = new ethers.providers.JsonRpcProvider(rpcURL)
                const marketplaceContract = new ethers.Contract(MARKETPLACE_ADDRESS_V2, KOMPETEMarketplaceV2ABI, provider)
                const paymentContract = new ethers.Contract(PAYMENT_ADDRESS, ERC20, provider)

                const userProvider = new ethers.providers.Web3Provider(walletProvider)
                const signer = userProvider.getSigner()
                const address = await signer.getAddress()

                if (!isMatchableWallet(address)) {
                    setInformationStatus({type: INFORMATION.ERROR, text: ERROR.WALLET_CONNECTED_MATCH_INVALID})
                    return
                }

                const [decimals, nonce, marketplaceInfo, lastBlock] = await Promise.all([
                    paymentContract.decimals(),
                    marketplaceContract.nonces(address),
                    marketplaceContract.getMarketplaceInfo(),
                    provider.getBlock("latest"),
                ])

                const price = ethers.utils.parseUnits(offerPrice, decimals)
                const seconds = expiration.value * 24 * 60 * 60
                const deadline = ethers.BigNumber.from(lastBlock.timestamp).add(seconds)
                let allowance = await paymentContract.allowance(address, addresses.MARKETPLACE_ADDRESS_V2)

                if (allowance.lt(price)) {
                    await paymentContract
                        .connect(signer)
                        .approve(addresses.MARKETPLACE_ADDRESS_V2, ethers.constants.MaxUint256)
                }

                const offerInitialData = {
                    buyer: address,
                    tokenContract: COLLECTION_ADDRESS,
                    tokenId: Number(currentTokenId),
                    amount: Number(count),
                    price,
                    paymentToken: PAYMENT_ADDRESS,
                    deadline,
                    nonce,
                }

                const domain = {
                    name: marketplaceInfo.name,
                    version: marketplaceInfo.version,
                    chainId: Number(currentChainId),
                    verifyingContract: MARKETPLACE_ADDRESS_V2,
                }

                const message = {...offerInitialData, chainId: currentChainId}

                const signature = await signer._signTypedData(domain, MAKE_OFFER_TYPE, message)
                const hash = ethers.utils._TypedDataEncoder.hash(domain, MAKE_OFFER_TYPE, message);
                const recoveredAddress = ethers.utils.recoverAddress(hash, signature);

                if (!isMatchableWallet(recoveredAddress)) {
                    setInformationStatus({type: INFORMATION.ERROR, text: ERROR.WALLET_SIGN_FAILED})
                    return
                }

                let response

                if (isMakeOfferModal) {
                    response = await makeOffer({
                        signature,
                        currency: "kompete",
                        offer: offerInitialData,
                        deadline: deadline.toNumber(),
                    }).unwrap()
                } else {
                    response = await updateOffer({
                        signature,
                        offer: offerInitialData,
                        deadline: deadline.toNumber(),
                        order_id: currentModal.data.id,
                    }).unwrap()
                }

                if (response.success) {
                    const type = isMakeOfferModal ? INTERACTION.MAKE_OFFER : INTERACTION.EDIT_OFFER
                    const text = isMakeOfferModal ? SUCCESS.MAKE_OFFER : SUCCESS.EDIT_OFFER
                    setInformationStatus({type: INFORMATION.SUCCESS, text})
                    setActionDetails({
                        type,
                        isSuccess: true,
                        ...(!isMakeOfferModal ? {
                            data: {
                                id: currentModal.data.id,
                                signature,
                                price: offerPrice,
                                order: offerInitialData,
                                deadline: deadline.toNumber() * 1000,
                            }
                        } : {}),
                    })

                    setTimeout(() => {
                        setCurrentModal({status: STEPS_STATUS.CLOSE})
                    }, 2000)
                } else {
                    setInformationStatus({type: INFORMATION.ERROR, text: ERROR.WRONG})
                }
            } catch (error) {
                switch (error.code) {
                    case "ACTION_REJECTED":
                    case WALLET_ERROR_CODE.USER_REJECT_REQUEST:
                        setInformationStatus({type: INFORMATION.ERROR, text: ERROR.USER_DENIED_SIGNATURE})
                        break
                    case "CALL_EXCEPTION":
                        setInformationStatus({type: INFORMATION.ERROR, text: ERROR.TRANSACTION_FAILED})
                        break
                    default:
                        setInformationStatus({type: INFORMATION.ERROR, text: ERROR.WRONG})
                }
            }
        })
    }

    const inventoryName = isMakeOfferModal ? name : currentModal.data.inventory.name
    const inventoryTokenId = isMakeOfferModal ? tokenId : currentModal.data.inventory.token_id
    const modalTitle = isMakeOfferModal ? "makeOffer" : "editOffer"

    const initialDisabledButton = isPending || informationStatus.type === INFORMATION.SUCCESS
    const isConfirmButtonDisabled = initialDisabledButton || offerPrice > userData.kompeteBalance || !expiration || !isConnectedAddressCorrect || !isConnectedChainCorrect

    return (
        <Modal
            isOpen={true}
            size={SIZE.SMALL}
            title={t(modalTitle)}
            onClose={() => !initialDisabledButton ? setCurrentModal({status: STEPS_STATUS.CLOSE}) : null}
        >
            <div className="bazaar-current-item_offer-modal">
                <span className="bazaar_current-item_offer-modal_under-title">{t("offerModalSubTitle")}</span>
                <div className="bazaar_current-item_offer-modal_item-info">
                    <InfoItem name="item" value={inventoryName}/>
                    <InfoItem name="tokenId" value={inventoryTokenId}/>
                    {!isMakeOfferModal &&
                        <React.Fragment>
                            <InfoItem name="price" value={`${currentModal.data.price} KOMPETE`}/>
                            <InfoItem
                                name="expiration"
                                value={format(new Date(currentModal.data.expiration_date), 'yyyy-MM-dd')}
                            />
                        </React.Fragment>}
                </div>
                {isMakeOfferModal &&
                    <div className="bazaar_current-item_offer-modal_item-info">
                        <InfoItem name="quantity" value={<Counter max={10} setCount={setCount} count={count}/>}/>
                    </div>
                }
                <div className="bazaar_current-item_offer-modal_inps">
                    <InputV2
                        value={offerPrice}
                        name="amount"
                        type="number"
                        size={SIZE.MEDIUM}
                        label={t("offerPrice")}
                        onChange={(e) => amountHandler(e)}
                        variant={offerPrice > userData.kompeteBalance ? INPUT_VARIANT.ERROR : INPUT_VARIANT.DEFAULT}
                    />
                    <span>{t("yourBalance")}: {userData.kompeteBalance.toFixed(2)} KOMPETE</span>
                    <SelectDropDownDisplay
                        size="md"
                        value={expiration}
                        isPrepareOptionsForLang={true}
                        label={t("offerExpiration")}
                        optionsList={LISTING_DAY_OPTIONS}
                        onChange={(e) => setExpiration(e.target.value.value)}
                    />
                </div>
                {isPending ? <Process/> : <Information
                    type={informationStatus.type}
                    text={informationStatus.text}
                />}
                <div className="bazaar_current-item_offer-modal_btns">
                    <ButtonV2
                        size={SIZE.MEDIUM}
                        disabled={initialDisabledButton}
                        onClick={() => setCurrentModal({status: STEPS_STATUS.CLOSE})}
                    >
                        cancel
                    </ButtonV2>
                    <ButtonV2
                        size={SIZE.MEDIUM}
                        onClick={handleOffer}
                        variant={BUTTON_VARIANT.RED}
                        disabled={isConfirmButtonDisabled}
                    >
                        confirm
                    </ButtonV2>
                </div>
            </div>
        </Modal>
    )
}

export default BazaarCurrentItemOfferModal